/**
 * Phone normalization helpers — UI shows pure 10-digit Indian mobiles
 * (no +91 prefix), backend stores E.164. These helpers form the
 * mapping layer at form boundaries.
 *
 * Display contract: 10 digits, numeric only, max 10 chars.
 * Storage contract: "+91XXXXXXXXXX" (E.164, what Twilio expects).
 *
 * Re-exported by `PhoneInput10` for the UI affordance (sticky +91 chip).
 */

/** Coerce any raw phone string ("+91 9876543210", "919876543210",
 * "09876543210", "9876543210") down to the pure-10-digit Indian
 * mobile number used in form fields. Returns "" if not a valid Indian
 * mobile number after stripping.
 */
export function to10Digit(raw) {
    if (!raw) return "";
    const digits = String(raw).replace(/\D/g, "");
    if (digits.length === 10) return digits;
    if (digits.length === 11 && digits.startsWith("0")) return digits.slice(1);
    if (digits.length === 12 && digits.startsWith("91")) return digits.slice(2);
    if (digits.length === 13 && digits.startsWith("091")) return digits.slice(3);
    // Last-resort: if we have more than 10 digits, take the trailing 10
    if (digits.length > 10) return digits.slice(-10);
    return digits; // partial input while user is still typing
}

/** Convert a 10-digit display value into the E.164 format the backend
 * expects. Returns "" if input isn't a complete 10-digit number.
 */
export function toE164(input) {
    const ten = to10Digit(input);
    if (ten.length !== 10) return "";
    return `+91${ten}`;
}

/** True if `input` is exactly 10 digits — used to enable/disable
 * submit buttons.
 */
export function isValid10Digit(input) {
    return to10Digit(input).length === 10;
}
